import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { MapPinned, Trash2, Wand2 } from 'lucide-react';
import {
  ParcelCreateInputSchema,
  type GeoJsonPolygon,
  type GeoJsonMultiPolygon,
  type ParcelCreateInput,
  type ParcelDto,
  type RoadsFeatureCollection,
} from '@forest/contracts';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { Spinner } from '@/components/ui/Spinner';
import { RegionsCombobox } from '@/features/regions/RegionsCombobox';
import { useEntitatiSilvice } from '@/features/regions/useRegions';
import { useCreateParcel, useParcelCount, useUpdateParcel } from './useParcels';
import { useUiStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/sessionStore';
import { apiError } from '@/lib/api-client';
import {
  buildAutofill,
  generateCodProiect,
  generateProjectUcfKey,
  type Autofill,
} from './autofill';

// Lazy-loaded: the Geoman + turf bundle only ships when the user opts into
// drawing geometry from the create dialog.
const GeometryEditorDialog = lazy(() =>
  import('./geometry-editor/GeometryEditorDialog').then((m) => ({
    default: m.GeometryEditorDialog,
  })),
);

type Props = {
  open: boolean;
  onClose: () => void;
  /** Existing parcel → edit mode; null → create mode. */
  parcel: ParcelDto | null;
  /**
   * Create-mode only. When true, the geometry editor opens immediately
   * on dialog mount so the user draws first, then lands on a form
   * pre-populated from the polygon (area + reverse-geocoded admin
   * fields + user-profile defaults). Cancel on the editor falls back
   * to the normal empty form.
   */
  startWithEditor?: boolean;
};

type FormShape = {
  projectUcfKey: string;
  projectId: string;
  sequenceId: string;
  raion: string;
  primarie: string;
  entitateSilvica: string;
  codProiect: string;
  ocolSilvic: string;
  suprafata: string;
  ucf: string;
  anul: string;
};

const EMPTY: FormShape = {
  projectUcfKey: '',
  projectId: '',
  sequenceId: '',
  raion: '',
  primarie: '',
  entitateSilvica: '',
  codProiect: '',
  ocolSilvic: '',
  suprafata: '',
  ucf: '',
  anul: String(new Date().getFullYear()),
};

/** Turn a ParcelDto into the text-shaped form state. */
function parcelToForm(p: ParcelDto): FormShape {
  return {
    projectUcfKey: p.projectUcfKey,
    projectId: p.projectId ?? '',
    sequenceId: p.sequenceId !== null ? String(p.sequenceId) : '',
    raion: p.raion,
    primarie: p.primarie,
    entitateSilvica: p.entitateSilvica ?? '',
    codProiect: p.codProiect ?? '',
    ocolSilvic: p.ocolSilvic ?? '',
    suprafata: String(p.suprafata),
    ucf: p.ucf,
    anul: String(p.anul),
  };
}

type GeometryDraft = {
  geometry: GeoJsonPolygon | GeoJsonMultiPolygon;
  roads: RoadsFeatureCollection | null;
};

/** Coerce the string-only form state into the numeric ParcelCreateInput. */
function formToInput(
  values: FormShape,
  draft: GeometryDraft | null,
): ParcelCreateInput {
  const parsed: ParcelCreateInput = {
    projectUcfKey: values.projectUcfKey.trim(),
    raion: values.raion.trim(),
    primarie: values.primarie.trim(),
    ucf: values.ucf.trim(),
    suprafata: Number(values.suprafata),
    anul: Number(values.anul),
    ...(values.sequenceId ? { sequenceId: Number(values.sequenceId) } : {}),
    ...(values.projectId ? { projectId: values.projectId.trim() } : {}),
    ...(values.entitateSilvica
      ? { entitateSilvica: values.entitateSilvica.trim() }
      : {}),
    ...(values.codProiect ? { codProiect: values.codProiect.trim() } : {}),
    ...(values.ocolSilvic ? { ocolSilvic: values.ocolSilvic.trim() } : {}),
    ...(draft
      ? {
          geometry: draft.geometry,
          geometryType: draft.geometry.type,
          ...(draft.roads ? { roads: draft.roads } : {}),
        }
      : {}),
  };
  // Validate once more so the zod error shape is available if something slipped through.
  return ParcelCreateInputSchema.parse(parsed);
}

function outerRingVertexCount(geom: GeoJsonPolygon | GeoJsonMultiPolygon): number {
  if (geom.type === 'Polygon') {
    const ring = geom.coordinates[0] ?? [];
    // Ring is closed — subtract the duplicated first vertex.
    return ring.length > 0 ? ring.length - 1 : 0;
  }
  // MultiPolygon: count the first polygon's outer ring only.
  const ring = geom.coordinates[0]?.[0] ?? [];
  return ring.length > 0 ? ring.length - 1 : 0;
}

function holeCount(geom: GeoJsonPolygon | GeoJsonMultiPolygon): number {
  if (geom.type === 'Polygon') {
    return Math.max(0, geom.coordinates.length - 1);
  }
  return Math.max(0, (geom.coordinates[0]?.length ?? 0) - 1);
}

export function ParcelFormDialog({
  open,
  onClose,
  parcel,
  startWithEditor,
}: Props) {
  const { t, i18n } = useTranslation(['database', 'common']);
  const isEdit = parcel !== null;
  const createMut = useCreateParcel();
  const updateMut = useUpdateParcel();
  const pushToast = useUiStore((s) => s.pushToast);
  const me = useAuthStore((s) => s.user);
  // `parcels?page=1&limit=1` — only reads pagination.total so payload is
  // tiny. Used to seed the next projectId on create mode.
  const parcelCountQ = useParcelCount();

  // Entitate silvica is a constrained select on this form, not a
  // free-text combobox. The allowed list depends on the caller's role:
  //   - SUPER_ADMIN / CONTROLOR: full canonical list from
  //     EntitateSilvica reference table (they may pick anything).
  //   - ADMINISTRATOR / AJUTOR_ADMINISTRATOR: only entities they're
  //     assigned to (`me.assignedEntitatiSilvice`). Auto-fill from
  //     `buildAutofill` (which keys off the drawn polygon's centroid)
  //     remains the suggested default; if it doesn't match an allowed
  //     entry the form clamps to the first allowed value.
  const isPrivileged = me?.role === 'SUPER_ADMIN' || me?.role === 'CONTROLOR';
  const entitatiQ = useEntitatiSilvice();
  const allowedEntities = useMemo<string[]>(() => {
    if (isPrivileged) {
      return (entitatiQ.data?.items ?? []).map((e) => e.name);
    }
    return [...(me?.assignedEntitatiSilvice ?? [])];
  }, [isPrivileged, entitatiQ.data, me?.assignedEntitatiSilvice]);

  // Geometry draft captured from the scratch editor before the parcel is
  // created. Only relevant in create mode; edit mode keeps the existing
  // "Editează geometria" dropdown path.
  const [geometryDraft, setGeometryDraft] = useState<GeometryDraft | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [autofillPending, setAutofillPending] = useState(false);
  const [autofillApplied, setAutofillApplied] = useState<Autofill | null>(null);
  const canDrawGeometry =
    !isEdit && (me?.role === 'SUPER_ADMIN' || me?.role === 'ADMINISTRATOR');

  const runAutofill = async (
    poly: GeoJsonPolygon | GeoJsonMultiPolygon,
  ): Promise<void> => {
    setAutofillPending(true);
    try {
      const fill = await buildAutofill(
        poly,
        me,
        (i18n.language?.slice(0, 2) as 'ro' | 'ru' | 'en') ?? 'ro',
      );
      const current = getValues();
      const applied: Autofill = {};
      // Only fill fields the user hasn't touched. Empty strings = untouched.
      (Object.keys(fill) as Array<keyof Autofill>).forEach((key) => {
        const value = fill[key];
        if (!value) return;
        const existing = current[key as keyof FormShape];
        if (existing && existing.trim() !== '') return;
        setValue(key as keyof FormShape, value, { shouldDirty: true });
        applied[key] = value;
      });
      if (Object.keys(applied).length > 0) {
        setAutofillApplied(applied);
        pushToast({
          variant: 'success',
          message: t('database:form.autofill.applied', {
            count: Object.keys(applied).length,
          }),
        });
      }
    } finally {
      setAutofillPending(false);
    }
  };

  // We deliberately treat all inputs as strings (zodResolver would fight us
  // on numeric coercion) and hand off to a validating `formToInput` on
  // submit. Error messages for individual fields come from zod.
  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<FormShape>({
    defaultValues: EMPTY,
    // No resolver — we run ParcelCreateInputSchema manually in onSubmit
    // so the numeric/string mismatch is never a problem.
    resolver: undefined,
  });
  void zodResolver;

  useEffect(() => {
    if (open) {
      reset(parcel ? parcelToForm(parcel) : EMPTY);
      // Reset draft state on each open so "+ Poligon nou" starts clean.
      setGeometryDraft(null);
      setAutofillApplied(null);
      lastAutoKeyRef.current = '';
      // Geometry-first flow: jump straight into the editor on create so
      // the user draws before seeing text fields. Eligibility mirrors
      // the server-side `canMutateGeometry` gate.
      const canDraw =
        !parcel && (me?.role === 'SUPER_ADMIN' || me?.role === 'ADMINISTRATOR');
      setEditorOpen(Boolean(startWithEditor && canDraw));
    }
  }, [open, parcel, reset, startWithEditor, me?.role]);

  // ----------------------------------------------------------------
  // Auto-generate projectUcfKey from (projectId + primarie + raion + ucf)
  // in create mode. Kicks in whenever one of the source fields changes,
  // as long as the user hasn't typed a custom value into projectUcfKey.
  // We remember the last auto-generated value in a ref: if the current
  // input matches it, the user hasn't manually edited it and we're free
  // to regenerate; otherwise we back off.
  // ----------------------------------------------------------------
  const lastAutoKeyRef = useRef<string>('');
  // Same idea for `codProiect`: it's the projectUcfKey minus the UCF
  // tail. Auto-populated from (projectId + primarie + raion) in create
  // mode unless the user has already typed something custom.
  const lastAutoCodRef = useRef<string>('');
  useEffect(() => {
    if (isEdit) return; // projectUcfKey is immutable on edit anyway
    const sub = watch((values, info) => {
      const changed = info.name;
      if (!changed) return;
      if (changed === 'projectUcfKey' || changed === 'codProiect') return;

      // projectUcfKey watches all 4 source fields.
      if (['projectId', 'primarie', 'raion', 'ucf'].includes(changed)) {
        const current = (values.projectUcfKey ?? '').trim();
        if (current === '' || current === lastAutoKeyRef.current) {
          const next = generateProjectUcfKey({
            projectId: values.projectId,
            primarie: values.primarie,
            raion: values.raion,
            ucf: values.ucf,
          });
          lastAutoKeyRef.current = next;
          setValue('projectUcfKey', next, { shouldDirty: false });
        }
      }

      // codProiect watches the same triplet (UCF doesn't apply here).
      if (['projectId', 'primarie', 'raion'].includes(changed)) {
        const currentCod = (values.codProiect ?? '').trim();
        if (currentCod === '' || currentCod === lastAutoCodRef.current) {
          const next = generateCodProiect({
            projectId: values.projectId,
            primarie: values.primarie,
            raion: values.raion,
          });
          lastAutoCodRef.current = next;
          setValue('codProiect', next, { shouldDirty: false });
        }
      }
    });
    return () => sub.unsubscribe();
  }, [watch, setValue, isEdit]);

  // Auto-seed projectId from the live parcel count on create mode.
  // Leaves the field alone in edit mode or if the user already typed
  // something. The watch subscription above will pick the new value up
  // and regenerate projectUcfKey automatically.
  useEffect(() => {
    if (!open || isEdit) return;
    const total = parcelCountQ.data;
    if (typeof total !== 'number') return;
    const current = (getValues().projectId ?? '').trim();
    if (current !== '') return;
    setValue('projectId', String(total + 1), { shouldDirty: false });
  }, [open, isEdit, parcelCountQ.data, getValues, setValue]);

  // Clamp entitateSilvica to the allowed list. Runs whenever:
  //   - the dialog opens (initial value from a parcel might be out of
  //     scope for the current caller — fail-safe to in-scope first),
  //   - the autofill subscription writes a new entity suggestion that
  //     isn't in the allowed list,
  //   - the allowed list itself loads / changes.
  // ADMIN/AJUTOR with assignedEntitatiSilvice = ['X', 'Y'] and an
  // autofilled value of 'Z' (from nearest-parcel match in another
  // entity) lands on 'X'. SUPER_ADMIN/CONTROLOR's allowed list is the
  // full reference table so this rarely fires for them.
  useEffect(() => {
    if (!open || allowedEntities.length === 0) return;
    const fallback = allowedEntities[0];
    if (!fallback) return; // length check above keeps tsc happy
    const sub = watch((values, info) => {
      // React on entitateSilvica changes OR after first allowedEntities load.
      if (info.name && info.name !== 'entitateSilvica') return;
      const current = (values.entitateSilvica ?? '').trim();
      if (current && allowedEntities.includes(current)) return;
      setValue('entitateSilvica', fallback, { shouldDirty: false });
    });
    // Initial sync: if the form already has a value out of scope when
    // the subscription is first set up, clamp it.
    const current = (getValues().entitateSilvica ?? '').trim();
    if (!current || !allowedEntities.includes(current)) {
      setValue('entitateSilvica', fallback, { shouldDirty: false });
    }
    return () => sub.unsubscribe();
  }, [open, allowedEntities, watch, setValue, getValues]);

  const regenerateKey = () => {
    const values = getValues();
    const next = generateProjectUcfKey({
      projectId: values.projectId,
      primarie: values.primarie,
      raion: values.raion,
      ucf: values.ucf,
    });
    lastAutoKeyRef.current = next;
    setValue('projectUcfKey', next, { shouldDirty: true });
  };

  const onSubmit = async (values: FormShape) => {
    let input: ParcelCreateInput;
    try {
      input = formToInput(values, isEdit ? null : geometryDraft);
    } catch (e) {
      // Surface zod errors under their field names where possible.
      const err = e as { issues?: Array<{ path: Array<string>; message: string }> };
      if (Array.isArray(err.issues)) {
        for (const issue of err.issues) {
          const field = String(issue.path[0] ?? '') as keyof FormShape;
          if (field in EMPTY) setError(field, { message: issue.message });
        }
      }
      return;
    }

    try {
      if (isEdit && parcel) {
        const { projectUcfKey: _omit, ...patch } = input;
        void _omit;
        await updateMut.mutateAsync({ id: parcel.id, input: patch });
        pushToast({
          variant: 'success',
          message: t('database:form.updateSuccess', {
            label: `${input.raion} · ${input.primarie} · UCF ${input.ucf}`,
          }),
        });
      } else {
        await createMut.mutateAsync(input);
        pushToast({
          variant: 'success',
          message: t('database:form.createSuccess', {
            label: `${input.raion} · ${input.primarie} · UCF ${input.ucf}`,
          }),
        });
      }
      onClose();
    } catch (e) {
      const err = await apiError(e);
      pushToast({
        variant: 'error',
        message: t(
          isEdit ? 'database:form.updateFailed' : 'database:form.createFailed',
          { message: err.message },
        ),
      });
    }
  };

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isPending) onClose();
      }}
      title={isEdit ? t('database:form.editTitle') : t('database:form.createTitle')}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3" noValidate>
        {canDrawGeometry ? (
          <div className="rounded-lg border border-dashed border-forest-300 bg-forest-50/40 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">
                  {t('database:form.geometry.title')}
                </p>
                <p className="mt-0.5 text-xs text-slate-600">
                  {geometryDraft
                    ? t('database:form.geometry.draftSummary', {
                        vertices: outerRingVertexCount(geometryDraft.geometry),
                        holes: holeCount(geometryDraft.geometry),
                        roads: geometryDraft.roads?.features.length ?? 0,
                      })
                    : t('database:form.geometry.optional')}
                </p>
                {autofillPending ? (
                  <p className="mt-1 text-xs text-slate-500">
                    {t('database:form.autofill.pending')}
                  </p>
                ) : autofillApplied ? (
                  <p className="mt-1 text-xs text-emerald-700">
                    {t('database:form.autofill.summary', {
                      fields: Object.keys(autofillApplied).join(', '),
                    })}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-shrink-0 gap-2">
                {geometryDraft ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setGeometryDraft(null);
                      setAutofillApplied(null);
                    }}
                    disabled={isPending}
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    {t('database:form.geometry.clear')}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setEditorOpen(true)}
                  disabled={isPending}
                >
                  <MapPinned className="mr-1 h-4 w-4" />
                  {geometryDraft
                    ? t('database:form.geometry.editDraft')
                    : t('database:form.geometry.draw')}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <FormField
          label={t('database:form.field.projectUcfKey')}
          htmlFor="pf-key"
          error={errors.projectUcfKey?.message}
          hint={
            !isEdit
              ? t('database:form.hint.projectUcfKeyAuto')
              : t('database:form.hint.projectUcfKey')
          }
          required
        >
          <div className="flex gap-2">
            <Input
              id="pf-key"
              {...register('projectUcfKey', { required: true })}
              disabled={isEdit}
              className="flex-1"
            />
            {!isEdit ? (
              <Button
                type="button"
                variant="secondary"
                onClick={regenerateKey}
                disabled={isPending}
                title={t('database:form.regenerateKey')}
              >
                <Wand2 className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </FormField>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            label={t('database:form.field.raion')}
            htmlFor="pf-raion"
            error={errors.raion?.message}
            required
          >
            <RegionsCombobox
              kind="raion"
              id="pf-raion"
              defaultValue={parcel?.raion ?? ''}
              {...register('raion', { required: true })}
            />
          </FormField>
          <FormField
            label={t('database:form.field.primarie')}
            htmlFor="pf-prim"
            error={errors.primarie?.message}
            required
          >
            <RegionsCombobox
              kind="primarie"
              id="pf-prim"
              defaultValue={parcel?.primarie ?? ''}
              {...register('primarie', { required: true })}
            />
          </FormField>
          <FormField
            label={t('database:form.field.entitateSilvica')}
            htmlFor="pf-ent"
            error={errors.entitateSilvica?.message}
          >
            <Select id="pf-ent" {...register('entitateSilvica')}>
              {/* Empty option only when no allowed entities — keeps the
                  field selectable even on edge cases (e.g. SA before
                  reference data is imported). For ADMIN/AJUTOR with no
                  assigned entities, requireEntityForCreate already blocks
                  submission upstream. */}
              {allowedEntities.length === 0 ? <option value="">—</option> : null}
              {allowedEntities.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField
            label={t('database:form.field.ocolSilvic')}
            htmlFor="pf-ocol"
            error={errors.ocolSilvic?.message}
          >
            <RegionsCombobox
              kind="ocolSilvic"
              id="pf-ocol"
              defaultValue={parcel?.ocolSilvic ?? ''}
              {...register('ocolSilvic')}
            />
          </FormField>
          <FormField
            label={t('database:form.field.codProiect')}
            htmlFor="pf-cod"
            error={errors.codProiect?.message}
          >
            <Input id="pf-cod" {...register('codProiect')} />
          </FormField>
          <FormField
            label={t('database:form.field.projectId')}
            htmlFor="pf-pid"
            error={errors.projectId?.message}
            hint={!isEdit ? t('database:form.hint.projectIdAuto') : undefined}
          >
            <Input id="pf-pid" {...register('projectId')} />
          </FormField>
          <FormField
            label={t('database:form.field.ucf')}
            htmlFor="pf-ucf"
            error={errors.ucf?.message}
            required
          >
            <Input id="pf-ucf" {...register('ucf', { required: true })} />
          </FormField>
          <FormField
            label={t('database:form.field.sequenceId')}
            htmlFor="pf-seq"
            error={errors.sequenceId?.message}
          >
            <Input id="pf-seq" type="number" {...register('sequenceId')} />
          </FormField>
          <FormField
            label={t('database:form.field.suprafata')}
            htmlFor="pf-supr"
            error={errors.suprafata?.message}
            required
          >
            <Input
              id="pf-supr"
              type="number"
              step="0.01"
              inputMode="decimal"
              {...register('suprafata', { required: true })}
            />
          </FormField>
          <FormField
            label={t('database:form.field.anul')}
            htmlFor="pf-anul"
            error={errors.anul?.message}
            required
          >
            <Input
              id="pf-anul"
              type="number"
              {...register('anul', { required: true })}
            />
          </FormField>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isPending}
          >
            {t('common:action.cancel')}
          </Button>
          <Button type="submit" loading={isPending}>
            {isEdit ? t('database:form.saveEdit') : t('database:form.saveCreate')}
          </Button>
        </div>
      </form>

      {canDrawGeometry && editorOpen ? (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60">
              <Spinner />
            </div>
          }
        >
          <GeometryEditorDialog
            open
            mode="create"
            parcel={null}
            initialDraft={
              geometryDraft
                ? {
                    geometry: geometryDraft.geometry,
                    geometryType: geometryDraft.geometry.type,
                    roads: geometryDraft.roads,
                  }
                : null
            }
            onScratchSave={(poly, roads) => {
              const isFirstDraft = geometryDraft === null;
              setGeometryDraft({ geometry: poly, roads });
              // Only auto-populate on the FIRST scratch save — subsequent
              // re-edits must not clobber values the user may have typed.
              if (isFirstDraft) {
                void runAutofill(poly);
              }
            }}
            onClose={() => setEditorOpen(false)}
          />
        </Suspense>
      ) : null}
    </Dialog>
  );
}
